/**
 * Canonical signup email security rules.
 * Used by auth-service, frontend (authUtils), and mirrored in Supabase hook SQL.
 */

const DEFAULT_BLOCKED_DOMAINS = [
  "burpcollaborator.net",
  "oastify.com",
  "oastify.net",
  "interactsh.com",
  "oast.fun",
  "oast.pro",
  "oast.me",
  "oast.live",
  "canarytokens.com",
  "canarytokens.org",
  "requestbin.com",
  "webhook.site",
  "pipedream.net",
  "dnslog.cn",
  "tempmail.com",
  "guerrillamail.com",
  "mailinator.com",
  "10minutemail.com",
  "throwaway.email",
  "temp-mail.org",
  "maildrop.cc",
  "getnada.com",
  "yopmail.com",
  "trashmail.com",
];

const SUSPICIOUS_EMAIL_PATTERNS = [
  /^test\d*@/i,
  /^admin\d*@/i,
  /^root\d*@/i,
  /^[a-z]{8,}@[a-z]+\.(net|org)$/i,
  /^[\da-f]{8,}@/i,
];

const SUSPICIOUS_LOCAL_PART_REGEX =
  /pentest|pen[._-]?test|redteam|blueteam|bugbounty|security[._-]?test|vuln[._-]?scan|burpsuite|hacktest|osinttest/i;

const SUSPICIOUS_DISPLAY_NAME_REGEX =
  /pentest|pen\s*test|red\s*team|blue\s*team|bug\s*bounty|security\s*test|burp\s*suite/i;

/**
 * Safe env read for Node (auth-service) and browser (Vite has no `process` global).
 * @param {string} key
 * @returns {string | undefined}
 */
function readEnvString(key) {
  if (typeof process === "undefined" || !process.env) {
    return undefined;
  }
  const value = process.env[key];
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

/**
 * @returns {string[]}
 */
function getAllowedDomains() {
  const raw = readEnvString("ALLOWED_EMAIL_DOMAINS");
  if (!raw) return [];
  return raw
    .split(",")
    .map((d) => d.trim().toLowerCase())
    .filter(Boolean);
}

/**
 * Authorized signup emails that bypass blocklist (e.g. sanctioned pentest).
 * @returns {Set<string>}
 */
function getAllowedSignupEmails() {
  const raw =
    readEnvString("ALLOWED_SIGNUP_EMAILS") || readEnvString("PENTEST_ALLOWLIST_EMAILS") || "";
  if (!raw) return new Set();
  return new Set(
    raw
      .split(",")
      .map((e) => e.trim().toLowerCase())
      .filter(Boolean)
  );
}

/**
 * @returns {string[]}
 */
function getBlockedDomains() {
  const extra = readEnvString("BLOCKED_EMAIL_DOMAINS");
  const extraDomains = extra
    ? extra
        .split(",")
        .map((d) => d.trim().toLowerCase())
        .filter(Boolean)
    : [];
  return [...new Set([...DEFAULT_BLOCKED_DOMAINS, ...extraDomains])];
}

/**
 * @param {string} localPart
 * @returns {boolean}
 */
function matchesSuspiciousLocalPart(localPart) {
  if (!localPart || typeof localPart !== "string") return false;
  return SUSPICIOUS_LOCAL_PART_REGEX.test(localPart);
}

/**
 * @param {string} displayName
 * @returns {boolean}
 */
function matchesSuspiciousDisplayName(displayName) {
  if (!displayName || typeof displayName !== "string") return false;
  return SUSPICIOUS_DISPLAY_NAME_REGEX.test(displayName.trim());
}

/**
 * @param {string} email
 * @param {{ displayName?: string }} [options]
 * @returns {{ allowed: boolean, reason: string | null, category?: string, domain?: string, pattern?: string }}
 */
function validateEmailSignupSecurity(email, options = {}) {
  if (!email || typeof email !== "string") {
    return { allowed: false, reason: "Invalid email format" };
  }

  const normalizedEmail = email.toLowerCase().trim();
  const domain = normalizedEmail.split("@")[1];
  const localPart = normalizedEmail.split("@")[0] || "";

  if (!domain) {
    return { allowed: false, reason: "Invalid email format" };
  }

  if (getAllowedSignupEmails().has(normalizedEmail)) {
    return { allowed: true, reason: null };
  }

  const allowedDomains = getAllowedDomains();
  if (allowedDomains.length > 0 && allowedDomains.includes(domain)) {
    return { allowed: true, reason: null };
  }

  if (getBlockedDomains().includes(domain)) {
    return {
      allowed: false,
      reason: "Email domain not allowed for registration",
      category: "blocked_domain",
      domain,
    };
  }

  if (matchesSuspiciousLocalPart(localPart)) {
    return {
      allowed: false,
      reason: "Email pattern not allowed for registration",
      category: "suspicious_pattern",
      pattern: "pentest_identity",
    };
  }

  if (options.displayName && matchesSuspiciousDisplayName(options.displayName)) {
    return {
      allowed: false,
      reason: "Display name not allowed for registration",
      category: "suspicious_pattern",
      pattern: "pentest_display_name",
    };
  }

  for (const pattern of SUSPICIOUS_EMAIL_PATTERNS) {
    if (pattern.test(normalizedEmail)) {
      return {
        allowed: false,
        reason: "Email pattern not allowed for registration",
        category: "suspicious_pattern",
      };
    }
  }

  return { allowed: true, reason: null };
}

/**
 * Same rules as signup; invite-specific user-facing messages.
 * @param {string} email
 * @param {{ displayName?: string }} [options]
 * @returns {{ allowed: boolean, reason: string | null, category?: string, domain?: string, pattern?: string }}
 */
function validateInvitableEmailSecurity(email, options = {}) {
  const result = validateEmailSignupSecurity(email, options);
  if (result.allowed) {
    return result;
  }

  if (result.category === "blocked_domain" && result.domain) {
    return {
      ...result,
      reason: `Temporary or disposable email addresses (${result.domain}) are not allowed for invitations. Please use a personal email such as Gmail or Outlook.`,
    };
  }

  if (result.category === "suspicious_pattern") {
    return {
      ...result,
      reason:
        "This email pattern is not allowed for security reasons. Please use a standard personal email address.",
    };
  }

  return {
    ...result,
    reason: result.reason || "This email address is not allowed for invitations.",
  };
}

/** @alias validateInvitableEmailSecurity */
const validateInviteEmailSecurity = validateInvitableEmailSecurity;

export {
  DEFAULT_BLOCKED_DOMAINS,
  SUSPICIOUS_EMAIL_PATTERNS,
  SUSPICIOUS_LOCAL_PART_REGEX,
  SUSPICIOUS_DISPLAY_NAME_REGEX,
  getAllowedDomains,
  getAllowedSignupEmails,
  getBlockedDomains,
  matchesSuspiciousLocalPart,
  matchesSuspiciousDisplayName,
  validateEmailSignupSecurity,
  validateInvitableEmailSecurity,
  validateInviteEmailSecurity,
};
